import type { AssignedRole, Candidate, Participation, SlotKey } from './api';
import {
  type AutofillMode,
  type ForcedReason,
  type ScoreBreakdown,
  type ScorePolicy,
  forcedReason,
  hasGreenFlag,
  isForcedPick,
  isRedCarded,
  isSoftForce,
  scoreBreakdown,
  selectionScore,
} from './selectionScore';

/**
 * Why a specific candidate ended up on the plan. Used for the preview UI.
 */
export type PickReason =
  | { kind: 'forced'; forced: ForcedReason }
  | { kind: 'soft' }
  | { kind: 'strong'; rank: number; total: number }
  | { kind: 'weak'; rank: number; total: number };

export type PlannedPick = {
  candidate: Candidate;
  breakdown: ScoreBreakdown;
  slot: SlotKey;
  role: AssignedRole;
  position: number;
  reason: PickReason;
};

export type PlannedSkip = {
  candidate: Candidate;
  breakdown: ScoreBreakdown;
  reason:
    | { kind: 'red_carded' }
    | { kind: 'capacity_full'; slot: SlotKey }
    | { kind: 'no_open_slot' };
};

export type AutofillPlan = {
  action: 'even' | 'strong' | 'weak' | 'strong_a_weak_b' | 'weak_a_strong_b';
  slot?: SlotKey;
  picks: PlannedPick[];
  skipped: PlannedSkip[];
};

function assignRolesByRank(
  picks: Candidate[],
  startersAssigned: number,
  subsAssigned: number,
): Map<number, AssignedRole> {
  const byM1Desc = (a: Candidate, b: Candidate) =>
    (b.latestSnapshot?.m1Power ?? 0) - (a.latestSnapshot?.m1Power ?? 0);
  const byStrength = [...picks].sort(byM1Desc);
  const roles: AssignedRole[] = byStrength.map((c, i) => {
    // R5 / R4 / never-sub are hard forced to starter, regardless of power.
    if (isForcedPick(c)) return 'starter';
    if (hasGreenFlag(c)) return 'starter';
    const rank = i + 1;
    if (rank <= 10) return 'starter';
    return rank % 2 === 1 ? 'starter' : 'sub';
  });
  let starterCount =
    startersAssigned + roles.filter((r) => r === 'starter').length;
  // Look for a starter slot we can drop to sub. Try in order:
  //   1. ordinary ranked starters
  //   2. green-flag starters (soft force)
  //   3. never-sub starters (hard force, but demotable before violating cap)
  // R5/R4 forced picks always keep their starter role.
  const findVictim = (allow: 'ranked' | 'green' | 'neverSub') => {
    for (let i = byStrength.length - 1; i >= 0; i--) {
      if (roles[i] !== 'starter') continue;
      const c = byStrength[i];
      const forced = forcedReason(c);
      if (forced === 'r5' || forced === 'r4') continue;
      if (forced === 'neverSub' && allow !== 'neverSub') continue;
      if (hasGreenFlag(c) && allow === 'ranked') continue;
      return i;
    }
    return -1;
  };
  while (starterCount > 20) {
    let victim = findVictim('ranked');
    if (victim === -1) victim = findVictim('green');
    if (victim === -1) victim = findVictim('neverSub');
    if (victim === -1) break;
    roles[victim] = 'sub';
    starterCount--;
  }
  const out = new Map<number, AssignedRole>();
  byStrength.forEach((c, i) => out.set(c.player.id, roles[i]));
  return out;
}

function reasonForRankedIndex(
  mode: AutofillMode,
  idx: number,
  total: number,
): PickReason {
  return mode === 'strong'
    ? { kind: 'strong', rank: idx + 1, total }
    : { kind: 'weak', rank: idx + 1, total };
}

function reasonForCandidate(
  c: Candidate,
  mode: AutofillMode,
  idxInRest: number,
  restTotal: number,
): PickReason {
  const forced = isForcedPick(c);
  const soft = isSoftForce(c);
  if (forced) {
    if (c.player.rank === 'R5') return { kind: 'forced', forced: 'r5' };
    if (c.player.rank === 'R4') return { kind: 'forced', forced: 'r4' };
    return { kind: 'forced', forced: 'neverSub' };
  }
  if (soft) return { kind: 'soft' };
  return reasonForRankedIndex(mode, idxInRest, restTotal);
}

/**
 * Plan the "Even both slots" distribution: applicants from A + B pools,
 * ranked by forced → soft → score desc, distributed zig-zag across slots
 * while respecting each player's own registration and per-slot capacity.
 */
export function planDistributeEven(opts: {
  eventType: 'canyon_storm' | 'desert_storm';
  candidatesA: Candidate[];
  candidatesB: Candidate[];
  participations: Participation[];
  policy: ScorePolicy;
}): AutofillPlan {
  const { eventType, candidatesA, candidatesB, participations, policy } = opts;
  const byId = new Map<number, { c: Candidate; a: boolean; b: boolean }>();
  for (const c of candidatesA) {
    if (!c.appliedThisSlot || c.assignedHere || c.assignedOther) continue;
    byId.set(c.player.id, { c, a: true, b: false });
  }
  for (const c of candidatesB) {
    if (!c.appliedThisSlot) continue;
    const existing = byId.get(c.player.id);
    if (existing) existing.b = true;
    else if (!c.assignedHere && !c.assignedOther) {
      byId.set(c.player.id, { c, a: false, b: true });
    }
  }
  const skipped: PlannedSkip[] = [];
  const pool = [...byId.values()]
    .filter((e) => {
      if (isRedCarded(e.c)) {
        skipped.push({
          candidate: e.c,
          breakdown: scoreBreakdown(e.c, policy),
          reason: { kind: 'red_carded' },
        });
        return false;
      }
      return true;
    })
    .sort((x, y) => {
      const fx = isForcedPick(x.c) ? 2 : isSoftForce(x.c) ? 1 : 0;
      const fy = isForcedPick(y.c) ? 2 : isSoftForce(y.c) ? 1 : 0;
      if (fx !== fy) return fy - fx;
      return selectionScore(y.c, policy) - selectionScore(x.c, policy);
    });

  const totalCap = {
    A:
      30 -
      participations.filter(
        (p) => p.assignedSlot === 'A' && p.assignedRole != null,
      ).length,
    B:
      30 -
      participations.filter(
        (p) => p.assignedSlot === 'B' && p.assignedRole != null,
      ).length,
  };
  const assignedBySlot: Record<SlotKey, Candidate[]> = { A: [], B: [] };
  let prefer: SlotKey = 'A';
  const isCs = eventType === 'canyon_storm';
  const tryPlace = (slot: SlotKey, c: Candidate) => {
    if (totalCap[slot] <= 0) return false;
    totalCap[slot]--;
    assignedBySlot[slot].push(c);
    return true;
  };
  for (const entry of pool) {
    const playerPref =
      isCs && entry.c.player.preferredCsSlot
        ? (entry.c.player.preferredCsSlot as SlotKey)
        : null;
    const first: SlotKey = playerPref ?? prefer;
    const second: SlotKey = first === 'A' ? 'B' : 'A';
    const canFirst = first === 'A' ? entry.a : entry.b;
    const canSecond = second === 'A' ? entry.a : entry.b;
    let placed = false;
    if (canFirst) placed = tryPlace(first, entry.c);
    if (!placed && canSecond) placed = tryPlace(second, entry.c);
    if (!placed) {
      skipped.push({
        candidate: entry.c,
        breakdown: scoreBreakdown(entry.c, policy),
        reason: { kind: 'no_open_slot' },
      });
    }
    if (!playerPref) prefer = prefer === 'A' ? 'B' : 'A';
  }

  const picks: PlannedPick[] = [];
  for (const slot of ['A', 'B'] as SlotKey[]) {
    const picksHere = assignedBySlot[slot];
    const startersHere = participations.filter(
      (p) => p.assignedSlot === slot && p.assignedRole === 'starter',
    ).length;
    const subsHere = participations.filter(
      (p) => p.assignedSlot === slot && p.assignedRole === 'sub',
    ).length;
    const roleByPlayer = assignRolesByRank(picksHere, startersHere, subsHere);
    // Rank within the ranked-rest bucket (for reason display), computed
    // per-slot from the ordering the pool was placed in.
    const restForRank = picksHere.filter((c) => !isForcedPick(c) && !isSoftForce(c));
    const restRank = new Map<number, number>();
    restForRank.forEach((c, i) => restRank.set(c.player.id, i));
    let sPos = startersHere;
    let bPos = subsHere;
    for (const c of picksHere) {
      const role = roleByPlayer.get(c.player.id)!;
      const position = role === 'starter' ? ++sPos : ++bPos;
      const idxInRest = restRank.get(c.player.id) ?? 0;
      picks.push({
        candidate: c,
        breakdown: scoreBreakdown(c, policy, { slot }),
        slot,
        role,
        position,
        reason: reasonForCandidate(c, 'strong', idxInRest, restForRank.length),
      });
    }
  }

  return { action: 'even', picks, skipped };
}

/**
 * Plan the per-slot autofill: rank applicants (forced → soft → rest by
 * score), take up to remaining capacity, assign roles by strength.
 */
export function planSlotAutofill(opts: {
  mode: AutofillMode;
  slot: SlotKey;
  candidates: Candidate[];
  startersAssigned: number;
  subsAssigned: number;
  policy: ScorePolicy;
}): AutofillPlan {
  const { mode, slot, candidates, startersAssigned, subsAssigned, policy } = opts;
  const applicants = candidates.filter(
    (c) => c.appliedThisSlot && !c.assignedHere && !c.assignedOther,
  );
  const skipped: PlannedSkip[] = [];
  const usable: Candidate[] = [];
  for (const c of applicants) {
    if (isRedCarded(c)) {
      skipped.push({
        candidate: c,
        breakdown: scoreBreakdown(c, policy, { slot }),
        reason: { kind: 'red_carded' },
      });
    } else {
      usable.push(c);
    }
  }
  const forced: Candidate[] = [];
  const soft: Candidate[] = [];
  const rest: Candidate[] = [];
  for (const c of usable) {
    if (isForcedPick(c)) forced.push(c);
    else if (isSoftForce(c)) soft.push(c);
    else rest.push(c);
  }
  const byScoreDesc = (a: Candidate, b: Candidate) =>
    selectionScore(b, policy, { slot }) - selectionScore(a, policy, { slot });
  const byScoreAsc = (a: Candidate, b: Candidate) =>
    selectionScore(a, policy, { slot }) - selectionScore(b, policy, { slot });
  forced.sort(byScoreDesc);
  soft.sort(byScoreDesc);
  rest.sort(mode === 'strong' ? byScoreDesc : byScoreAsc);
  const ranked = [...forced, ...soft, ...rest];

  const remainingStarter = Math.max(0, 20 - startersAssigned);
  const remainingSub = Math.max(0, 10 - subsAssigned);
  const capacity = remainingStarter + remainingSub;
  const picks = ranked.slice(0, capacity);
  for (const c of ranked.slice(capacity)) {
    skipped.push({
      candidate: c,
      breakdown: scoreBreakdown(c, policy, { slot }),
      reason: { kind: 'capacity_full', slot },
    });
  }

  const roleByPlayer = assignRolesByRank(picks, startersAssigned, subsAssigned);
  const restTotal = rest.length;
  const restRank = new Map<number, number>();
  rest.forEach((c, i) => restRank.set(c.player.id, i));
  let sPos = startersAssigned;
  let bPos = subsAssigned;
  const planned: PlannedPick[] = [];
  for (const c of picks) {
    const role = roleByPlayer.get(c.player.id)!;
    const position = role === 'starter' ? ++sPos : ++bPos;
    const idxInRest = restRank.get(c.player.id) ?? 0;
    planned.push({
      candidate: c,
      breakdown: scoreBreakdown(c, policy, { slot }),
      slot,
      role,
      position,
      reason: reasonForCandidate(c, mode, idxInRest, restTotal),
    });
  }

  return { action: mode, slot, picks: planned, skipped };
}
